import {computed, DestroyRef, effect, Injectable, signal, untracked} from '@angular/core'
import { injectLogger } from '@/services/logger.service'
import {BetslipWsApi} from '@/api/websocket/betslip-ws-api'
import {BetslipRiskInfoDto} from '@/api/player-api/models/betslip-risk-info-dto'
import {CreateBetslipDto} from '@/api/player-api/models/create-betslip-dto'
import {BetslipConverterService} from '@/features/betslip/betslip-create/lib/converters/betslip-converter.service'
import {AuthService} from '@/services/auth.service'
import {BetslipCreateService} from '@/features/betslip/betslip-create/betslip-create.service'
import {BetslipData} from '@/features/betslip/betslip-create/lib/models/betslip-data'
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop'
import {catchError, combineLatest, debounceTime, distinctUntilChanged, EMPTY, from, map, Observable, of, switchMap} from 'rxjs'

/** Long enough that ticking through System Options sends one request, not one per tick. */
export const RISK_DEBOUNCE_MS = 50

// TODO: DEV-MOCK — remove BetslipRiskDevSource and NO_RISK_INFO with the dev limits mocks
// (features/dev-tools/betslip-workbench/README.md)
/** Answers a risk request in place of the back end. */
export type BetslipRiskDevSource = (payload: CreateBetslipDto) => BetslipRiskInfoDto

/** A reply with no ceilings and no problems — what is left once a dev source's reply is withdrawn. */
const NO_RISK_INFO: BetslipRiskInfoDto = {
	problemCode: 0,
	problems: [],
	riskLimitDto: {upperLimit: 0, vipUpperLimit: 0, upperPayLimit: 0},
	selections: [],
	systems: []
}

/**
 * Asks the back end for risk info whenever its inputs change (ADR 0001): Selections added or
 * removed, any Selection's odds or banker, the Betslip Type, the ticked System Options — never on a
 * stake change — and on login, since a logged-out player gets none. Changes in quick succession send
 * one request; a reply a newer request has overtaken is dropped; a failed request is logged, and the
 * next change asks again.
 */
@Injectable({providedIn: 'root'})
export class BetslipRiskService {

	private readonly logger = injectLogger('BetslipRiskService')

	// TODO: DEV-MOCK — remove with the dev limits mocks (features/dev-tools/betslip-workbench/README.md)
	/**
	 * Dev builds only: answers risk requests instead of the back end, logged in or not, until the
	 * back end sends real ceilings. Set by the dev tools; nothing sets it in production.
	 */
	readonly devSource = signal<BetslipRiskDevSource | undefined>(undefined)

	constructor(
		private readonly betslipCreateService: BetslipCreateService,
		private readonly betslipConverterService: BetslipConverterService,
		private readonly betslipWsApi: BetslipWsApi,
		private readonly destroyRef: DestroyRef,
		private readonly authService: AuthService
	) {
		const riskInputs$ = this.betslipCreateService.betslipData$.pipe(map(riskInputs), distinctUntilChanged())
		const loggedIn$ = toObservable(computed(() => this.authService.isLoggedIn))
		// TODO: DEV-MOCK — drop devSource$ and its slot below with the dev limits mocks (betslip-workbench/README.md)
		const devSource$ = toObservable(this.devSource)

		combineLatest([riskInputs$, loggedIn$, devSource$])
			.pipe(
				debounceTime(RISK_DEBOUNCE_MS),
				switchMap(([, loggedIn, devSource]) => this.getRiskInfo(loggedIn, devSource)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((betslipRiskInfoDto) => {
				this.logger.debug('On risk info', betslipRiskInfoDto)
				this.betslipCreateService.mergeRemoteProblems(betslipRiskInfoDto)
			})

		// TODO: DEV-MOCK — remove this effect with the dev limits mocks (betslip-workbench/README.md)
		// Once a dev source is unset, its reply goes from the betslip too, or its ceilings would keep
		// capping the stake; the back end is then asked as above. Nothing runs while none was set.
		let hadDevSource = false
		effect(() => {
			if (this.devSource()) {
				hadDevSource = true
				return
			}
			if (!hadDevSource) {
				return
			}

			hadDevSource = false
			untracked(() => this.betslipCreateService.mergeRemoteProblems(NO_RISK_INFO))
		})
	}

	private getRiskInfo(loggedIn: boolean, devSource: BetslipRiskDevSource | undefined): Observable<BetslipRiskInfoDto> {
		const betslipData = this.betslipCreateService.getBetslipData()

		if (betslipData.selections.length === 0) {
			return EMPTY
		}

		const payload = this.betslipConverterService.createPayload(betslipData)

		// TODO: DEV-MOCK — the dev source answers alone, logged in or not; remove with the dev limits mocks
		if (devSource) {
			return of(devSource(payload))
		}

		if (!loggedIn) {
			return EMPTY
		}

		return from(this.betslipWsApi.betslipRiskInfo(payload, this.destroyRef)).pipe(
			catchError((err) => {
				this.logger.error('Risk info request failed:', err)
				return EMPTY
			})
		)
	}
}

/** Everything the risk request depends on, and nothing that changes with the stake. */
function riskInputs(data: BetslipData): string {
	return JSON.stringify({
		betType: data.betType,
		selections: data.selections.map((s) => [s.matchId, s.oddsId, s.odds, !!s.isBanker]),
		systems: data.systems.filter((s) => s.checked).map((s) => s.count)
	})
}
